/* eslint-disable camelcase */
import { ParsedQuery } from 'query-string';
import { PageType } from '../../hooks/UrlManager/types';
import { Dictionary } from '../../../../types/globals';
import { GenericShopApiResponse } from '../../../common/apiAdapters/types';
import { NavigationBaseData } from '../../components/Generic/NavigationItem';
import { BaseLookDetailRawData, LookImageRawData } from '../lookDetail/types';
import {
    AppliedFilter,
    AppliedSorting,
    Filter,
    Sorting,
    SortingParameters,
} from 'Client/components/Generic/FilterAndSortBox/types';
import { ProductInterface, ProductResponse } from 'Common/appshell_types';

export interface ApiResponseAppliedFilters {
    [key: string]: {
        type: string;
        values: Array<string> | Object;
    };
}

export interface ProductsResponseData {
    filters?: Array<Filter>;
    sorting?: Array<Sorting>;
    didYouMean?: any;
    totalCount?: number;
    products?: Array<ProductResponse>;
    crossCategoryNavigation?: CrossCategoryNavigationResponse;
    appliedSorting?: AppliedSorting;
    appliedFilters?: ApiResponseAppliedFilters;
    looksInfusions?: LooksInfusionApiResponse[];
    search_response_id: string;
}

export type ProductsResponse = GenericShopApiResponse<ProductsResponseData>;

export interface CrossCategoryNavigationCategoryResponse {
    id: string;
    name: string;
    image: {};
    isSelected: boolean;
    links: any[];
}

export interface CrossCategoryNavigationLinkResponse {
    rel: string;
    href: string;
}

export interface CrossCategoryNavigationResponse {
    categories: CrossCategoryNavigationCategoryResponse[];
    parentCategory: { isRoot: boolean; links: CrossCategoryNavigationLinkResponse[] };
}

export interface CrossCategoryNavigationParentCategory {
    isRoot: boolean;
    link: string;
}

export interface CrossCategoryNavigation {
    categories: NavigationBaseData[];
    parentCategory: CrossCategoryNavigationParentCategory;
}

export interface ProductsListLinks {
    rel: string;
    href: string;
}

export enum SecondaryProductsIdentifier {
    ALL_PRODUCTS = 'allProducts',
    NEW_PRODUCTS = 'newProducts',
}

export enum SearchType {
    EMPTY = '',
    FUZZY = 'fuzzy',
    STRICT = 'strict',
}

export interface BasicProductsInterface {
    products: ProductInterface[];
    alternativeProducts: ProductInterface[];
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    error?: any;
}

export interface ProductsState extends BasicProductsInterface {
    didYouMean: ReducedDidYouMeanInterface[];
    filters: Filter[];
    sorting: Sorting[];
    crossCategoryNavigation: CrossCategoryNavigation;
    pageTitle: string;
    cmsPageTitle: string;
    totalCount: number;
    identifier: string;
    links: ProductsListLinks[];
    pdpUrl: string;
    searchType: SearchType;
    isMoodImageHovered?: boolean;
    looksInfusions: LooksInfusion[] | null;
    trackingData?: {
        wwReqId: string;
        searchResponseId: string; // NDCG tracking data
    };
}

export interface GenericProductsState {
    identifier: string;
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    products: ProductInterface[];
    error?: any;
}

export interface SecondaryProductsState {
    products: { [identifier in SecondaryProductsIdentifier]: GenericProductsState };
}

export interface DidYouMeanInterface {
    title: string;
    links: LinkResponse[];
}

export interface ReducedDidYouMeanInterface {
    title: string;
    relLink: string;
}

export interface LinkResponse extends CrossCategoryNavigationLinkResponse {}

export interface HydratedFilterValue {
    id_catalog_brand: string;
    name: string;
    url_key: string;
}

export enum pathFilterNames {
    colors = 'facet_col',
    materials = 'facet_mat_filter',
    brands = 'facet_brand_keys',
    categories = 'facet_category_keys',
    sizes = 'facet_generic_size',
    styles = 'facet_styles',
    pattern = 'facet_pattern',
    form = 'facet_form',
    pillowType = 'facet_filter_pillow_type',
}

export interface FiltersWithHydratedValues extends Filter {
    hydratedValues: Dictionary<HydratedFilterValue>;
}

export interface BrandsFilters extends FiltersWithHydratedValues {}
export interface CategoryFilters extends FiltersWithHydratedValues {}

export const isBrandsFilter = (input: Filter): input is BrandsFilters => input.name === pathFilterNames.brands;
export const isCategoriesFilter = (input: Filter): input is CategoryFilters =>
    input.name === pathFilterNames.categories;

export enum FilterType {
    query = 'query',
    materials = 'materials',
    colors = 'colors',
    brands = 'brands',
    styles = 'styles',
    categories = 'categories',
    sizes = 'sizes',
    pattern = 'pattern',
    form = 'form',
    pillowType = 'pillowType',
}

export interface ListingPageUrlState {
    appliedFilters: AppliedFilter<FilterType>[];
    listingPageKey: string;
    listingPageType: PageType;
    sortingParams: SortingParameters;
    search?: string;
    strictSearch?: string;
    query: ParsedQuery;
    page: number;
}
export interface PriceFormatConfigOptions {
    countryCode: string;
    currency: string;
}

export interface Options extends PriceFormatConfigOptions {
    includeLooksInfusions: boolean;
}

export interface LooksInfusionApiResponse extends BaseLookDetailRawData {
    relatedSku: string;
    genericImagePortrait: LookImageRawData;
}

export interface LooksInfusion {
    relatedSku: string;
    portraitImg: string;
    name: string;
    slug: string;
    color: string;
    trackingName: string;
}

export * from 'Common/appshell_types/PDPProduct';
